import type {
    Blog,
    BlogItem,
    ContentfulEvent,
    ContentfulEventItem,
    ContentfulOfficeItem,
    News,
    NewsItem,
    Office,
    PressRelease,
    PressReleaseItem,
    SpeakerAuthor,
    SpeakerAuthorItem,
    ContentfulTeamItem,
    ContentfulTeamFields,
    PrivacyPolicyItem,
    PrivacyPolicy,
    TermsOfUse,
    TermsOfUseItem,
    ContentfulLovingJunctionItem,
    ContentfulLovingJunctionMember,
} from '$lib/types/contentful';
import type { Action } from 'svelte/types/runtime/action';

import type { RequestEvent } from '@sveltejs/kit';

export function jsonResponse<T>(data: T, status = 200): Response {
    return new Response(JSON.stringify(data), {
        headers: { 'Content-Type': 'application/json' },
        status,
    });
}

export async function jsonRequest<T>(request: RequestEvent): Promise<T> {
    try {
        return await request.request.json();
    } catch {
        throw 'Invalid JSON body';
    }
}

const parseSuggestedContent = (contents) => {
    return contents.map((content) => {
        switch (content.sys?.contentType?.sys?.id) {
            case 'event':
                return parseEvent(content);
            case 'blog':
                return parseBlog(content);
            case 'pressRelease':
                return parsePressRelease(content);
            case 'news':
                return parseNews(content);
            case 'team':
                return parseTeam(content);
            default:
                break;
        }
        return null;
    });
};

export const parseAuthor = (rawAuthor: SpeakerAuthorItem): SpeakerAuthor => {
    return {
        ...rawAuthor.fields,
        id: rawAuthor.sys.id,
        type: 'speakerauthor',
        profileImageUrl: rawAuthor.fields.profileImage
            ? rawAuthor.fields.profileImage.fields.file.url
            : null,
    };
};

export const parseEvent = (rawEvent: ContentfulEventItem): ContentfulEvent => {
    return {
        ...rawEvent.fields,
        id: rawEvent.sys.id,
        type: 'event',
        updatedAt: rawEvent.sys.updatedAt,
        eventImageUrl: rawEvent.fields.eventImage
            ? rawEvent.fields.eventImage.fields.file.url
            : null,
        isFeatured: rawEvent.metadata.tags.some((tag) => tag.sys.id === 'featured'),
    };
};

export const parseLovingJunctionMember = (
    rawLovingJunctionMember: ContentfulLovingJunctionItem,
): ContentfulLovingJunctionMember => {
    return {
        description: rawLovingJunctionMember.fields.description,
        jobTitle: rawLovingJunctionMember.fields.jobTitle,
        name: rawLovingJunctionMember.fields.name,
        photoUrl: rawLovingJunctionMember.fields.photo.fields.file.url,
        photoIconUrl: rawLovingJunctionMember.fields.photoIcon.fields.file.url,
        logoUrl: rawLovingJunctionMember.fields.logo.fields.file.url,
    };
};

export const parseOffice = (rawOffice: ContentfulOfficeItem): Office => {
    return {
        ...rawOffice.fields,
        id: rawOffice.sys.id,
        type: 'office',
        updatedAt: rawOffice.sys.updatedAt,
    };
};

export const parseBlog = (rawBlog: BlogItem): Blog => {
    return {
        ...rawBlog.fields,
        id: rawBlog.sys.id,
        type: 'blog',
        heroImageUrl: rawBlog.fields.heroImage ? rawBlog.fields.heroImage.fields.file.url : null,
        vignetteUrl: rawBlog.fields.vignette ? rawBlog.fields.vignette.fields.file.url : null,
        suggestedContent: rawBlog.fields?.suggestedContent
            ? parseSuggestedContent(rawBlog.fields.suggestedContent)
            : [],
        author: rawBlog.fields.author ? parseAuthor(rawBlog.fields.author) : null,
        tags: rawBlog.metadata?.tags.length ? rawBlog.metadata.tags : null,
        seoDescription: rawBlog.fields.seoDescription || null,
        updatedAt: rawBlog.sys.updatedAt,
    };
};

export const parsePressRelease = (rawPressRelease: PressReleaseItem): PressRelease => {
    return {
        ...rawPressRelease.fields,
        id: rawPressRelease.sys.id,
        type: 'pressRelease',
        heroImageUrl: rawPressRelease.fields.heroImage
            ? rawPressRelease.fields.heroImage.fields.file.url
            : null,
        vignetteUrl: rawPressRelease.fields.vignette
            ? rawPressRelease.fields.vignette.fields.file.url
            : null,
        author: rawPressRelease.fields.author ? parseAuthor(rawPressRelease.fields.author) : null,
        tags: rawPressRelease.metadata?.tags.length ? rawPressRelease.metadata.tags : null,
        suggestedContent: rawPressRelease.fields?.suggestedContent
            ? parseSuggestedContent(rawPressRelease.fields.suggestedContent)
            : [],
        updatedAt: rawPressRelease.sys.updatedAt,
    };
};

export const parseNews = (rawNews: NewsItem): News => {
    return {
        ...rawNews.fields,
        id: rawNews.sys.id,
        type: 'news',
        imageUrl: rawNews.fields.image ? rawNews.fields.image.fields.file.url : null,
        mediaLogoUrl: rawNews.fields.mediaLogo ? rawNews.fields.mediaLogo.fields.file.url : null,
    };
};

export const parseTeam = (rawTeam: ContentfulTeamItem): ContentfulTeamFields => {
    return {
        ...rawTeam.fields,
        id: rawTeam.sys.id,
        teamId: rawTeam.fields.teamId,
        type: 'team',
        name: rawTeam.fields.name,
        members: rawTeam.fields.members.map((member) => parseAuthor(member)),
    };
};

export const parsePrivacyPolicy = (rawPolicy: PrivacyPolicyItem): PrivacyPolicy => {
    return {
        ...rawPolicy.fields,
    };
};

export const parseTermsOfUse = (rawTerms: TermsOfUseItem): TermsOfUse => {
    return {
        ...rawTerms.fields,
    };
};

export function clickOutside(node: Element, action: () => void): Action<Element, undefined> {
    let action_ = action;

    function click(e: MouseEvent): void {
        if (node.contains(e.target as Node)) {
            return;
        }

        action_();
    }

    window.addEventListener('mousedown', click);

    return {
        destroy() {
            window.removeEventListener('mousedown', click);
        },
        update(action: () => void) {
            action_ = action;
        },
    };
}

export async function scrollIntoView(
    scrollToThis: Element,
    block: 'start' | 'center' | 'end',
    behavior: 'smooth' | undefined,
): Promise<void> {
    if (scrollToThis) {
        scrollToThis.scrollIntoView({ behavior, block });
    }
}

import { format } from 'date-fns';
//Dec 1, 2021
export const dateFormat = (date: string | number | Date) => format(new Date(date), 'LLL d, yyyy');

export const wheel = (node, options) => {
    let { scrollable } = options;

    const handler = (e) => {
        console.log('scrollabnle', scrollable);
        if (!scrollable) {
            e.preventDefault();
        } else {
            node.removeEventListener('wheel', handler, { passive: false });
        }
    };

    node.addEventListener('wheel', handler, { passive: false });

    return {
        update(options) {
            scrollable = options.scrollable;
        },
        destroy() {
            node.removeEventListener('wheel', handler, { passive: false });
        },
    };
};

export const parseFilteringQueryForPublicationDateField = (
    url: URL,
): { 'fields.publicationDate[gt]'?: string; 'fields.publicationDate[lte]'?: string } => {
    let queryFiltering = {};
    const filteredYear = url.searchParams.get('year') || null;
    if (filteredYear) {
        queryFiltering = {
            'fields.publicationDate[gt]': new Date(Number(filteredYear), 0, 1)
                .toISOString()
                .split('T')[0],
            'fields.publicationDate[lte]': new Date(Number(filteredYear) + 1, 0, 1)
                .toISOString()
                .split('T')[0],
        };
    }

    return queryFiltering;
};

export const parseFilteringQueryForDateField = (
    url: URL,
): { 'fields.date[gt]'?: string; 'fields.date[lte]'?: string } => {
    let queryFiltering = {};
    const filteredYear = url.searchParams.get('year') || null;
    if (filteredYear) {
        queryFiltering = {
            'fields.date[gt]': new Date(Number(filteredYear), 0, 1).toISOString().split('T')[0],
            'fields.date[lte]': new Date(Number(filteredYear) + 1, 0, 1)
                .toISOString()
                .split('T')[0],
        };
    }

    return queryFiltering;
};
